import { useEffect, useState } from "react";
import {
  ISiteFacet,
  ISiteTag,
  useSetSiteTagsMutation,
} from "../../../../redux/api/site-classification/siteClassificationAPI";
import { IHttpError } from "../../../../shared/oversight-core/interfaces/http-errror";
import AppSelect, {
  Option,
} from "../../../../shared/oversight-core/ui-elements/app-select/app-select";
import ModalContainer from "../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../../shared/oversight-core/utils/toast";

interface IProps {
  show: boolean;
  plantId: string;
  facets: ISiteFacet[];
  siteTags: ISiteTag[];
  onClose: () => void;
  onSaved: () => void;
}

const NONE = "";

type Selection = Record<string, string[]>;

const selectionOf = (siteTags: ISiteTag[]): Selection =>
  Object.fromEntries(siteTags.map((tag) => [tag.facetId, [...tag.valueIds]]));

// Offered: the facet's current values, plus any archived value this site already carries -
// it may keep one, but not be newly given it.
const optionsOf = (facet: ISiteFacet, carried: string[]): Option[] =>
  facet.values
    .filter((value) => !value.archived || carried.includes(value.id))
    .map((value) => ({
      value: value.id,
      label: value.archived ? `${value.label} (archived)` : value.label,
    }));

// Sets a site's tags from its organization's picklists. Archived facets are not offered; whatever
// the site carries from one is kept as it is.
const EditSiteTagsModal = (props: IProps) => {
  const { show, plantId, facets, siteTags, onClose, onSaved } = props;
  const [selection, setSelection] = useState<Selection>(selectionOf(siteTags));
  const [setSiteTags, { isLoading }] = useSetSiteTagsMutation();

  // The site page polls every few seconds and hands down a new siteTags array each time, so the
  // selection follows it only while the modal is closed, and an open edit is never overwritten.
  // Closing resets the selection to the saved tags, so an unsaved draft doesn't come back.
  useEffect(() => {
    if (!show) {
      setSelection(selectionOf(siteTags));
    }
  }, [show, siteTags]);

  const editable = facets.filter((facet) => !facet.archived);
  const missingRequired = editable.filter(
    (facet) => facet.required && (selection[facet.id] ?? []).length === 0,
  );

  const save = () => {
    const tags: ISiteTag[] = facets
      .map((facet) => ({
        facetId: facet.id,
        valueIds: facet.archived
          ? (siteTags.find((tag) => tag.facetId === facet.id)?.valueIds ?? [])
          : (selection[facet.id] ?? []),
      }))
      .filter((tag) => tag.valueIds.length > 0);

    setSiteTags({ plantId, siteTags: tags })
      .unwrap()
      .then(() => {
        showSuccessMessage("Site tags saved");
        onSaved();
        onClose();
      })
      .catch((error: IHttpError) =>
        showErrorMessage(error?.message || "The site's tags could not be saved."),
      );
  };

  return (
    <ModalContainer
      show={show}
      onClose={onClose}
      onCancel={onClose}
      onConfirm={save}
      title="Edit tags"
      confirmButtonText="Save"
      confirmButtonVariant="blue"
      size="modal-md"
      isLoading={isLoading}
      disabled={missingRequired.length > 0}
    >
      <div className="d-flex flex-column gap-3">
        {editable.map((facet) => {
          const carried = selection[facet.id] ?? [];
          const options = optionsOf(facet, carried);

          if (facet.cardinality === "MULTI") {
            return (
              <AppSelect
                key={facet.id}
                label={facet.name}
                placeholder={`No ${facet.name.toLowerCase()}`}
                options={options}
                selectedValue={options.filter((option) =>
                  carried.includes(option.value),
                )}
                onChangeOption={(selected) =>
                  setSelection((current) => ({
                    ...current,
                    [facet.id]: ((selected as unknown as Option[]) ?? []).map(
                      (option) => option.value,
                    ),
                  }))
                }
                isMultiSelect
                closeMenuOnSelect={false}
                isRequired={facet.required}
              />
            );
          }

          const singleOptions = facet.required
            ? options
            : [{ value: NONE, label: "None" }, ...options];

          return (
            <AppSelect
              key={facet.id}
              label={facet.name}
              placeholder={`Choose a ${facet.name.toLowerCase()}`}
              options={singleOptions}
              selectedValue={singleOptions.find(
                (option) => option.value === (carried[0] ?? NONE),
              )}
              onChangeOption={(option) =>
                setSelection((current) => ({
                  ...current,
                  [facet.id]: option.value === NONE ? [] : [option.value],
                }))
              }
              isRequired={facet.required}
            />
          );
        })}
        {editable.every((facet) => facet.values.length === 0) && (
          <div className="text-light font-size-13">
            Your organization has no values to tag sites with yet. An
            organization administrator adds them in Settings, Site
            classification.
          </div>
        )}
        {missingRequired.length > 0 && (
          <div className="text-light font-size-12">
            {missingRequired.map((facet) => facet.name).join(", ")}{" "}
            {missingRequired.length === 1 ? "is" : "are"} required.
          </div>
        )}
      </div>
    </ModalContainer>
  );
};

export default EditSiteTagsModal;
